import React, { useEffect, useMemo } from 'react'
import { LocaleProvider } from '@douyinfe/semi-ui'
import { IntlProvider } from 'react-intl'
import { BrowserRouter } from 'react-router-dom'
import zh_CN from '@douyinfe/semi-ui/lib/es/locale/source/zh_CN'
import en_GB from '@douyinfe/semi-ui/lib/es/locale/source/en_GB'
import useStore from './store/common/global'
import { localeConfig } from './locales'
import RenderRouter from './router'
import { usePrimaryHueSync } from './hooks/usePrimaryHueSync'
import { useContainerWidthSync } from './hooks/useContainerWidthSync'
import { useDocumentMeta } from './hooks/useDocumentMeta'
import './App.scss'

function App() {
	const locale = useStore((state) => state.locale)

	const getLocale = useMemo(() => {
		if (locale === 'en_GB') {
			return en_GB
		} else if (locale === 'zh_CN') {
			return zh_CN
		}
	}, [locale])

	return (
		<LocaleProvider locale={getLocale}>
			<IntlProvider locale={locale.split('_')[0]} messages={localeConfig[locale]}>
				<BrowserRouter>
					<SyncWrappers />
				</BrowserRouter>
			</IntlProvider>
		</LocaleProvider>
	)
}

const SyncWrappers = () => {
	usePrimaryHueSync()
	useContainerWidthSync()
	useDocumentMeta()

	// React 挂载完成后淡出初始加载动画，避免白屏
	// 不再使用硬编码 500ms，而是监听 transitionend 在淡出完成时移除 DOM
	useEffect(() => {
		const loadingEl = document.getElementById('app-loading')
		if (!loadingEl) return

		loadingEl.classList.add('is-hidden')

		const onTransitionEnd = (e: TransitionEvent) => {
			if (e.propertyName !== 'opacity') return
			loadingEl.removeEventListener('transitionend', onTransitionEnd)
			if (loadingEl.parentNode) loadingEl.remove()
		}
		loadingEl.addEventListener('transitionend', onTransitionEnd)

		// 兜底：如果 transitionend 因浏览器后台标签等原因不触发，
		// 1s 后强制移除，避免元素常驻
		const fallback = window.setTimeout(() => {
			loadingEl.removeEventListener('transitionend', onTransitionEnd)
			if (loadingEl.parentNode) loadingEl.remove()
		}, 1000)

		return () => {
			loadingEl.removeEventListener('transitionend', onTransitionEnd)
			window.clearTimeout(fallback)
		}
	}, [])

	return <RenderRouter />
}

export default App
